import { type FC } from "react";
import { Box, styled } from "@mui/material";
import Image from "next/image";
import type { Product } from "@/entities/products";
import { ThumbnailFormatType } from "@/types/graphql/site.generated";

// Референс показывает промо-видео карточки цен (ProductPlayer) — у нас
// продукт промо не имеет, поэтому здесь всегда статичная обложка (preview)
// 16:9 (см. task-8-brief.md, п.2). Фон-заглушка под обложкой — токен
// background.elevated (макет 1b), скругление совпадает с карточками темы.
const MediaContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  paddingTop: "56.25%",
  backgroundColor: theme.palette.background.elevated,
  borderRadius: 14,
  overflow: "hidden",
}));

type Thumbnails = NonNullable<Product["preview"]>["thumbnails"];

// Порт ThumbnailCardMedia.js:17-34: выбор миниатюры по цепочке
// MAXRES → HIGH → MEDIUM → DEFAULT (первый найденный формат в этом
// порядке, а не "любой доступный"), с приоритетом webpUrl над url.
function pickThumbnailUrl(
  thumbnails: Thumbnails | undefined,
): string | undefined {
  if (!thumbnails?.length) return undefined;
  const order = [
    ThumbnailFormatType.MaxresType,
    ThumbnailFormatType.HighType,
    ThumbnailFormatType.MediumType,
    ThumbnailFormatType.DefaultType,
  ];
  for (const format of order) {
    const thumbnail = thumbnails.find((t) => t.format === format);
    if (thumbnail) return String(thumbnail.webpUrl || thumbnail.url);
  }
  return undefined;
}

export const ProductPreviewMedia: FC<{ product: Product }> = ({ product }) => {
  const image = product.preview;
  const imageUrl = image
    ? pickThumbnailUrl(image.thumbnails) || String(image.url)
    : undefined;

  return (
    <MediaContainer>
      {imageUrl && (
        <Image
          src={imageUrl}
          alt={product.title}
          fill
          style={{ objectFit: "cover" }}
          sizes="(max-width: 900px) 100vw, 40vw"
          unoptimized
        />
      )}
    </MediaContainer>
  );
};
